import FightIntroPopup from "@/components/FightSystem/FightIntroPopup.vue";
import { PIGGY_BANK_QUEST_ID } from "@/config/constants";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import Communicator, { AnalyticsEvents } from "@/libs/AppCommunicatior";
import useOfferStore from "@/store/useOfferStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useShopStore from "@/store/useShopStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import type { TileCoordinates } from "@/types/tile";
import { TileLabelSkin } from "@/types/tile";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import type SettlementCastleBuildingTileEntity from "./entities/buildings/SettlementCastleBuildingTileEntity";
import { ResourceType } from "./entities/config/resources";
import { settlementNames } from "./entities/config/settlements";
import { getNewUpgradesIdsFromLevel } from "./entities/config/upgrades";
import { QuestRepository } from "./QuestRepository";
import { ToastPlacement } from "./ToastMessage";
import { getUnlocksByLevel } from "./utils/resources";

class LevelManager {
	private rationsUnlockLevel: number = Number.MAX_SAFE_INTEGER;

	public init(): void {
		const settlementsStore = useSettlementsStore();
		const mainLevel = settlementsStore.mainLevel;
		if (mainLevel >= 4) {
			this.createSettlementHighlightLabels();
		}
		if (mainLevel >= 5) {
			const quest = useQuestStore().getActiveQuests.find(
				(q: QuestActive): boolean => q.id === PIGGY_BANK_QUEST_ID,
			);
			if (
				quest === undefined &&
				(useShopStore().piggyBankOffers?.length ?? 0) > 0
			) {
				useQuestStore().addQuest(PIGGY_BANK_QUEST_ID, 0);
			}
			if ((useShopStore().piggyBankOffers?.length ?? 0) === 0) {
				useQuestStore().finishQuest(PIGGY_BANK_QUEST_ID);
			}
		}

		this.rationsUnlockLevel =
			getUnlocksByLevel()?.[ResourceType.RATIONS] ??
			this.rationsUnlockLevel;

		if (mainLevel >= this.rationsUnlockLevel) {
			this.showSiegeSystemTutorialPopup();
		}
	}

	public levelUpSetup(): void {
		const userStore = useUserStore();
		const settlementsStore = useSettlementsStore();
		const mainLevel = settlementsStore.mainLevel;

		userStore.setPlayerLevel(mainLevel);
		userStore.setNewUpgradesIds(getNewUpgradesIdsFromLevel(mainLevel));
	}

	public onLevelUpFinish(): void {
		const settlementsStore = useSettlementsStore();

		const mainLevel = settlementsStore.mainLevel;
		const communicator = Communicator.getInstance();
		if (mainLevel === 2) {
			communicator.sendAnalyticsEvent(AnalyticsEvents.LEVEL_2_ACHIEVED, {
				level: 2,
			});
		}
		if (mainLevel === 3) {
			useOfferStore().checkAndShowOffer(true);
		}
		if (mainLevel === 4) {
			this.createSettlementHighlightLabels();
			this.showQTPopup();
		}
		if (mainLevel === 5) {
			communicator.sendAnalyticsEvent(AnalyticsEvents.LEVEL_5_ACHIEVED, {
				level: 5,
			});
		}
		if (mainLevel === 10) {
			this.resetDailyQuests();
		}
		if ([10, 18, 24].includes(mainLevel)) {
			this.showRateUsPopup();
		}
		QuestRepository.getInstance().initializeSideQuestsMarkers();

		if (mainLevel === this.rationsUnlockLevel) {
			this.showSiegeSystemTutorialPopup();
		}
	}

	private showRateUsPopup(): void {
		const userStore = useUserStore();
		if (userStore.appRated) return;

		const communicator = Communicator.getInstance();
		const popupStore = usePopupStore();
		if (!userStore.appRatedGemRewardClaimed) {
			let body = translate("ui.gem_reward_for_playing");
			body = body.replace("{number}", "<strong>100</strong>");
			body = body.replace("{icon}", "<span class='icon-gems-14'></span>");
			const { increase } = useResourcesStore();
			const callback = (): void => {
				userStore.setAppRatedGemRewardClaimed(true);
				increase(ResourceType.GEMS, 100, "rate-us-reward");
				useEmitter().emit(EmitterEvents.PhaserTileClick, {
					x: 0,
					y: 0,
					reward: {
						[ResourceType.GEMS.toLowerCase() as string]: 100,
					},
					placement: ToastPlacement.TOP_CENTER,
				});
				popupStore.hidePopup();
			};
			popupStore.showPopup({
				id: PopupDictionary.RATE_US,
				type: PopupType.RATE_US,
				heading: translate("ui.i_like_this"),
				body,
				skin: SkinTypes.LIGHT,
				closeable: false,
				actions: [
					{
						label: translate("button.no"),
						buttonType: StatusTypes.INFO,
						callback: (): void => {
							callback();
						},
					},
					{
						label: translate("button.yes_like"),
						buttonType: StatusTypes.PRIMARY,
						callback: (): void => {
							callback();

							useSocketService()
								.emitSaveRequest({
									stores: [EmitStores.USER],
								})
								.then((): void => {
									//
									communicator.openStore();
								});
						},
					},
				],
				height: 17.5,
				isCentered: true,
				isPaddingless: true,
			});
			return;
		}
		popupStore.showPopup({
			id: PopupDictionary.RATE_OUR_GAME,
			type: PopupType.RATE_US,
			heading: translate("ui.rate_our_game"),
			body: translate("ui.rate_prompt"),
			skin: SkinTypes.LIGHT,
			closeable: false,
			actions: [
				{
					label: translate("button.later"),
					buttonType: StatusTypes.INFO,
					callback: (): void => {
						popupStore.hidePopup();
					},
				},
				{
					label: translate("button.rate_now"),
					buttonType: StatusTypes.PRIMARY,
					callback: (): void => {
						popupStore.hidePopup();

						communicator.openStore();
					},
				},
			],
			height: 15.5,
			isCentered: true,
			isPaddingless: true,
		});
	}

	private showQTPopup(): void {
		const popupStore = usePopupStore();
		popupStore.showPopup({
			id: PopupDictionary.QUICK_TOOL,
			type: PopupType.QT,
			decoratedHeading: translate("ui.new_level"),
			skin: SkinTypes.DARK,
			maxHeight: 42,
		});
	}

	private createSettlementHighlightLabels(): void {
		const settlentIds = [5, 6, 7, 71];
		const tilerepo = useTileRepository();
		settlentIds.forEach((settlementId: number): void => {
			const settlement = settlementNames.get(settlementId);
			if (!settlement) return;
			const settlementTile = tilerepo.getTileEntityByTileCoordinates({
				x: settlement.x,
				y: settlement.y,
			}) as SettlementCastleBuildingTileEntity;
			if (settlementTile?.isRevealed === true) {
				// ak nie je viditelny, tak netreba ani label
				return;
			}
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: {
					x: settlement.x,
					y: settlement.y,
				} as TileCoordinates,
				skin: TileLabelSkin.SETTLEMENT_HIGHLIGHT,
				action: TileLabelAction.CREATE,
			});
		});
	}

	public resetDailyQuests(): void {
		const settlementsStore = useSettlementsStore();
		const mainLevel = settlementsStore.mainLevel;
		if (mainLevel < 10) return;
		useQuestStore().resetDailyQuests();
	}

	private showSiegeSystemTutorialPopup(): void {
		const sss = useSiegeSystemStore();
		if (sss.tutorialSeen) return;

		usePopupStore().showPopup({
			type: PopupType.INFO,
			skin: SkinTypes.DARK,
			isPaddingless: true,
			isCentered: true,
			isPriority: true,
			height: "auto",
			width: 23.875,
			component: FightIntroPopup,
			closeCallback: (): void => {
				sss.setTutorialSeen(true);
			},
		});
	}
}
export const levelManager = new LevelManager();
